// consult-enrichment.js — PURE, CLIENT-SIDE longitudinal enrichment engines for
// the consult. This is the ONLINE-PRIMARY (main-2) rebuild of the deleted
// disk-first server engines (server/opd/clinical-insights.mjs +
// server/opd/view-adapter.mjs enrichment block + server/opd/care-structures.mjs,
// removed in commit 374f95c on 2026-07-11). The COMPUTATION is ported verbatim
// so the owner-approved behaviour is preserved — only the DATA SOURCE changed:
// the engines used to read the disk store; here they receive already-fetched,
// RLS-scoped FHIR resources (the logged-in doctor's own token) from
// medplum-consult-enrichment.js and compute the same DTO the consult screens
// (ConsultCompact.jsx / MarkersChart.jsx) already read through
// OPDBridge.pick('encounter.markers|vitalSeries|insightsBar|insightsRanked|episode').
//
// PURE + never-throws: every public builder degrades a malformed input to an
// empty/tolerated shape, never a throw — so the consult can never break on this
// path (same fail-soft contract as the priorContext enrichment in
// medplum-encounter.js). No MedplumClient import here — a Node/vitest test feeds
// synthetic FHIR bundles directly.
//
// PROVENANCE of the ported logic (line refs are the pre-deletion source):
//   • CANON_LABS + interpretValue        — server/opd/lab-observations.mjs:80-215
//   • colorForValue / markerInsight /
//     vitalInsight / VITAL_BANDS /
//     MEANINGFUL_DELTA / directionOf /
//     trendNote / getTrends / getMarkers /
//     getVitalSeries / getWhatChanged     — server/opd/clinical-insights.mjs
//   • buildInsightsBar / buildInsightsRanked /
//     buildInsightsBlock / colorTrendPoints /
//     worstColor / changeKindScore / colorForChange — view-adapter.mjs:1177-1489
//   • formatGoal / formatCarePlan / computeGoalProgress — view-adapter.mjs:1601-1635
//     + care-structures.mjs:341-369

// ── LOINC / reference table (lab-observations.mjs CANON_LABS, verbatim) ──────
// A conservative Indian-OPD reference table — a triage aid, NOT a substitute for
// a lab's own quoted range. low/high = normal band; critLow/critHigh = panic.
export const CANON_LABS = Object.freeze({
  wbc: { loinc: '6690-2', display: 'Leukocytes (WBC/TLC)', unit: '10*3/uL', low: 4.0, high: 11.0, critLow: 2.0, critHigh: 30.0 },
  rbc: { loinc: '789-8', display: 'Erythrocytes (RBC)', unit: '10*6/uL', low: 4.2, high: 5.9 },
  platelets: { loinc: '777-3', display: 'Platelets', unit: '10*3/uL', low: 150, high: 410, critLow: 50, critHigh: 1000 },
  haemoglobin: { loinc: '718-7', display: 'Haemoglobin', unit: 'g/dL', low: 13.0, high: 17.0, critLow: 7.0, critHigh: 20.0 },
  hba1c: { loinc: '4548-4', display: 'Haemoglobin A1c', unit: '%', low: 4.0, high: 5.7, critHigh: 10.0 },
  fbs: { loinc: '1558-6', display: 'Fasting glucose', unit: 'mg/dL', low: 70, high: 100, critLow: 50, critHigh: 400 },
  totalCholesterol: { loinc: '2093-3', display: 'Total cholesterol', unit: 'mg/dL', low: 0, high: 200, critHigh: 300 },
  ldl: { loinc: '2089-1', display: 'LDL cholesterol', unit: 'mg/dL', low: 0, high: 100, critHigh: 190 },
  hdl: { loinc: '2085-9', display: 'HDL cholesterol', unit: 'mg/dL', low: 40, high: 100 },
  triglycerides: { loinc: '2571-8', display: 'Triglycerides', unit: 'mg/dL', low: 0, high: 150, critHigh: 500 },
  creatinine: { loinc: '2160-0', display: 'Creatinine', unit: 'mg/dL', low: 0.7, high: 1.3, critHigh: 4.0 },
  tsh: { loinc: '3016-3', display: 'Thyroid stimulating hormone (TSH)', unit: 'mIU/L', low: 0.4, high: 4.0, critLow: 0.01, critHigh: 50.0 },
  potassium: { loinc: '2823-3', display: 'Potassium', unit: 'mmol/L', low: 3.5, high: 5.1, critLow: 2.5, critHigh: 6.5 },
  sodium: { loinc: '2951-2', display: 'Sodium', unit: 'mmol/L', low: 135, high: 145, critLow: 120, critHigh: 160 },
  // Cardiology/renal markers the flagship consult surfaces. NT-proBNP has no
  // single universal cut-off (age-dependent) — we carry a conservative adult
  // upper bound so a rising trend still reads as a real move, and mark low=0.
  ntprobnp: { loinc: '33762-6', display: 'NT-proBNP', unit: 'pg/mL', low: 0, high: 125, critHigh: 2000 },
  egfr: { loinc: '62238-1', display: 'eGFR', unit: 'mL/min/1.73m2', low: 60, high: 120, critLow: 15 },
});

// LOINC → canonical key index (for loinc-only observations / interpretValue).
export const LOINC_INDEX = {};
for (const [key, meta] of Object.entries(CANON_LABS)) LOINC_INDEX[meta.loinc] = key;

/** Resolve a labKey OR a LOINC code to the canonical CANON_LABS key, or null. */
export function resolveLabKey(labKeyOrLoinc) {
  const raw = String(labKeyOrLoinc || '').trim();
  if (!raw) return null;
  if (CANON_LABS[raw]) return raw;
  if (LOINC_INDEX[raw]) return LOINC_INDEX[raw];
  const lc = raw.toLowerCase();
  return CANON_LABS[lc] ? lc : null;
}

/**
 * interpretValue(labKey|loinc, value) →
 *   { interpretation: 'normal'|'high'|'low'|'critical'|'unknown', refLow, refHigh }
 * Computed ONLY from the value against CANON_LABS — never fabricated. Unknown
 * metric → 'unknown'. (lab-observations.mjs:203-216, verbatim logic.)
 */
export function interpretValue(labKeyOrLoinc, value) {
  const key = resolveLabKey(labKeyOrLoinc);
  const meta = key ? CANON_LABS[key] : null;
  const n = Number(value);
  if (!meta || !Number.isFinite(n)) {
    return { interpretation: 'unknown', refLow: meta?.low ?? null, refHigh: meta?.high ?? null };
  }
  const { low, high, critLow, critHigh } = meta;
  if (critLow != null && n <= critLow) return { interpretation: 'critical', refLow: low ?? null, refHigh: high ?? null };
  if (critHigh != null && n >= critHigh) return { interpretation: 'critical', refLow: low ?? null, refHigh: high ?? null };
  if (high != null && n > high) return { interpretation: 'high', refLow: low ?? null, refHigh: high ?? null };
  if (low != null && n < low) return { interpretation: 'low', refLow: low ?? null, refHigh: high ?? null };
  return { interpretation: 'normal', refLow: low ?? null, refHigh: high ?? null };
}

// ── generic helpers ─────────────────────────────────────────────────────────
function safeArr(v) { return Array.isArray(v) ? v : []; }
function num(v) { if (v == null || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null; }
function isoDate(s) { return s ? String(s).slice(0, 10) : null; }

// ── colorForValue — the SHARED traffic-light (clinical-insights.mjs:156-206) ──
const COLOR_RANK = { amber: 0, yellow: 1, green: 2 };
const COLOR_SEVERITY = { amber: 'review', yellow: 'watch', green: 'normal' };

/**
 * colorForValue(labKey, value, prevValue?) → 'green' | 'yellow' | 'amber'.
 * PURE, never throws. GREEN = in-range OR out-but-improving vs prevValue; AMBER =
 * far-out / critical / worsening; YELLOW = mildly out with no direction. Unknown
 * metric/value → 'yellow' (never a confident green).
 */
export function colorForValue(labKey, value, prevValue) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'yellow';
  let interp, refLow, refHigh;
  try { ({ interpretation: interp, refLow, refHigh } = interpretValue(labKey, n)); } catch { return 'yellow'; }
  if (interp === 'normal') return 'green';
  if (interp === 'unknown') return 'yellow';
  if (interp === 'critical') return 'amber';
  const lo = Number.isFinite(Number(refLow)) ? Number(refLow) : null;
  const hi = Number.isFinite(Number(refHigh)) ? Number(refHigh) : null;
  let outBy = 0; let width = null;
  if (hi != null && n > hi) { outBy = n - hi; } else if (lo != null && n < lo) { outBy = lo - n; }
  if (lo != null && hi != null && hi > lo) width = hi - lo;
  const farThreshold = width != null ? width * 0.25 : Math.abs((hi != null && n > hi ? hi : lo) || 0) * 0.25;
  const far = outBy > 0 && farThreshold > 0 && outBy > farThreshold;
  const p = Number(prevValue);
  if (Number.isFinite(p)) {
    const target = hi != null && n > hi ? hi : (lo != null && n < lo ? lo : null);
    if (target != null) {
      const distNow = Math.abs(n - target);
      const distPrev = Math.abs(p - target);
      if (distNow < distPrev) return 'green';
      if (distNow > distPrev) return 'amber';
    }
  }
  return far ? 'amber' : 'yellow';
}

// ── per-marker / per-vital insight line (clinical-insights.mjs:223-304) ───────
export function markerInsight({ display, coloured, unit, labKey } = {}) {
  try {
    const pts = safeArr(coloured).filter((p) => p && p.value != null);
    if (!pts.length) return null;
    const last = pts[pts.length - 1];
    const first = pts[0];
    const u = unit ? ` ${unit}` : '';
    let interp = 'unknown'; let refLow = null; let refHigh = null;
    try { ({ interpretation: interp, refLow, refHigh } = interpretValue(labKey, last.value)); } catch { /* unknown */ }
    const moved = pts.length >= 2 && Number(first.value) !== Number(last.value);
    const spanTxt = moved ? `${first.value}→${last.value}${u}` : `${last.value}${u}`;
    if (last.color === 'green') {
      const wasOut = first && first.color && first.color !== 'green';
      if (moved && wasOut) return { text: `${display} ${spanTxt}, now at target`, color: 'green', severity: 'normal' };
      return null;
    }
    const sideRaw = interp === 'high' || (refHigh != null && Number(last.value) > Number(refHigh)) ? 'above'
      : interp === 'low' || (refLow != null && Number(last.value) < Number(refLow)) ? 'below' : null;
    const sideTxt = interp === 'critical' ? 'critical' : sideRaw ? `${sideRaw} range` : 'out of range';
    const action = last.color === 'amber' ? 'review' : 'watch';
    return { text: `${display} ${spanTxt} — ${sideTxt}, ${action}`, color: last.color, severity: COLOR_SEVERITY[last.color] || 'watch' };
  } catch { return null; }
}

const VITAL_BANDS = {
  systolic: { warnHigh: 130, amberHigh: 140, warnLow: 100, amberLow: 90, action: 'review BP' },
  diastolic: { warnHigh: 85, amberHigh: 90, warnLow: 60, amberLow: 55, action: 'review BP' },
  hr: { warnHigh: 100, amberHigh: 110, warnLow: 55, amberLow: 50, action: 'review pulse' },
  spo2: { warnLow: 94, amberLow: 92, action: 'review oxygenation' },
  temp: { warnHigh: 37.5, amberHigh: 38, warnLow: 35.5, amberLow: 35, action: 'review temperature' },
};

export function vitalInsight({ key, display, unit, points } = {}) {
  try {
    const band = VITAL_BANDS[key];
    const pts = safeArr(points).filter((p) => p && p.value != null);
    if (!band || !pts.length) return null;
    const last = Number(pts[pts.length - 1].value);
    const first = Number(pts[0].value);
    if (!Number.isFinite(last)) return null;
    const u = unit ? ` ${unit}` : '';
    const moved = pts.length >= 2 && first !== last;
    const spanTxt = moved ? `${first}→${last}${u}` : `${last}${u}`;
    let color = 'green'; let side = null;
    if ((band.amberHigh != null && last >= band.amberHigh) || (band.amberLow != null && last <= band.amberLow)) {
      color = 'amber'; side = band.amberHigh != null && last >= band.amberHigh ? 'high' : 'low';
    } else if ((band.warnHigh != null && last >= band.warnHigh) || (band.warnLow != null && last <= band.warnLow)) {
      color = 'yellow'; side = band.warnHigh != null && last >= band.warnHigh ? 'high' : 'low';
    }
    if (color === 'green') return null;
    const sideTxt = side === 'high' ? 'above range' : 'below range';
    const action = color === 'amber' ? band.action : 'watch';
    return { text: `${display} ${spanTxt} — ${sideTxt}, ${action}`, color, severity: COLOR_SEVERITY[color] || 'watch' };
  } catch { return null; }
}

// ── trend direction (clinical-insights.mjs:88-129) ───────────────────────────
const MEANINGFUL_DELTA = {
  hba1c: 0.5, fbs: 25, glucose: 25, ldl: 20, hdl: 8, totalCholesterol: 25, triglycerides: 40,
  creatinine: 0.3, tsh: 1.0, potassium: 0.5, sodium: 5, hb: 1.0, haemoglobin: 1.0,
  // Cardiology/renal markers.
  ntprobnp: 300, egfr: 5,
};
function meaningfulDeltaFor(labKey) { return MEANINGFUL_DELTA[labKey] != null ? MEANINGFUL_DELTA[labKey] : null; }

function directionOf(values, labKey) {
  if (!Array.isArray(values) || values.length < 3) return 'insufficient';
  const first = values[0];
  const last = values[values.length - 1];
  const delta = last - first;
  const band = meaningfulDeltaFor(labKey);
  const rel = Math.abs(first) * 0.05;
  const deadband = Math.max(rel, band != null ? band * 0.5 : 0);
  if (Math.abs(delta) <= deadband) return 'stable';
  return delta > 0 ? 'rising' : 'falling';
}

function trendNote({ labKey, display, unit, points, direction, delta }) {
  const u = unit ? ` ${unit}` : '';
  const last = points[points.length - 1];
  const first = points[0];
  const lastTxt = `${last.value}${u} on ${isoDate(last.date) || 'unknown date'}`;
  if (direction === 'insufficient') {
    return points.length === 1
      ? `Only one recorded ${display} value (${lastTxt}); need ≥3 to call a trend.`
      : `Only ${points.length} recorded ${display} values; need ≥3 to call a trend (latest ${lastTxt}).`;
  }
  if (direction === 'stable') return `${display} stable across ${points.length} readings (latest ${lastTxt}).`;
  const band = meaningfulDeltaFor(labKey);
  const dir = direction === 'rising' ? 'rising' : 'falling';
  const meaningful = band != null && delta != null && Math.abs(delta) >= band;
  const span = `${first.value}→${last.value}${u} (${isoDate(first.date)}→${isoDate(last.date)})`;
  const flag = meaningful ? ` — clinically meaningful (Δ${delta > 0 ? '+' : ''}${delta}${u}); review.` : ` (Δ${delta > 0 ? '+' : ''}${delta}${u}).`;
  return `${display} ${dir} ${span}${flag}`;
}

// ── FHIR Observation → the row shape the engines consume ─────────────────────
// Mirrors lab-observations.mjs shapeObservation, but from a plain (RLS-fetched)
// FHIR Observation resource. labKey resolves via the _healther bag first (mirror
// rows), then the LOINC index (what the seed + real devices write). Non-lab
// Observations return null so the lab bucket stays clean.
const INTERP_CODE = { N: 'normal', H: 'high', L: 'low', HH: 'critical', LL: 'critical' };
function healtherBag(o) {
  const ext = safeArr(o && o.extension).find((e) => e && /opd-healther$/.test(String(e.url || '')));
  if (ext && ext.valueString) { try { return JSON.parse(ext.valueString) || {}; } catch { return {}; } }
  return (o && o._healther) || {};
}
function interpOf(o, bag) {
  if (bag && bag.interpretation) return bag.interpretation;
  const c = o && o.interpretation && o.interpretation[0];
  const code = c && c.coding && c.coding[0] && c.coding[0].code;
  return (code && INTERP_CODE[code]) || (c && c.text) || 'unknown';
}
export function shapeLabObservation(o) {
  if (!o || o.resourceType !== 'Observation') return null;
  const bag = healtherBag(o);
  const isLab = bag.kind === 'lab-observation'
    || safeArr(o.category).some((c) => safeArr(c.coding).some((x) => x.code === 'laboratory'));
  if (!isLab) return null;
  const coding = (o.code && o.code.coding && o.code.coding[0]) || {};
  const loinc = coding.code || null;
  const labKey = bag.labKey || (loinc ? LOINC_INDEX[loinc] : null) || null;
  const vq = o.valueQuantity || {};
  const derivedFromDocId = bag.derivedFromDocId
    || (o.derivedFrom && o.derivedFrom[0] && String(o.derivedFrom[0].reference || '').replace(/^DocumentReference\//, '')) || null;
  return {
    id: o.id || null,
    loinc,
    labKey,
    display: (o.code && o.code.text) || coding.display || labKey || 'Lab result',
    value: vq.value ?? null,
    unit: vq.unit || (labKey && CANON_LABS[labKey]?.unit) || null,
    date: o.effectiveDateTime || (o.meta && o.meta.lastUpdated) || null,
    interpretation: interpOf(o, bag),
    sourceResourceId: o.id || null,
    derivedFromDocId,
    encounterId: (o.encounter && String(o.encounter.reference || '').replace(/^Encounter\//, '')) || null,
  };
}

/** Group patient-wide lab Observation ROWS (shapeLabObservation output) by
 *  canonical labKey, newest-first within each key (contract: fetch is newest
 *  first). De-dupes the same logical reading stored twice. */
function labRowsByKey(rows) {
  const byKey = new Map();
  const seen = new Set();
  for (const r of safeArr(rows)) {
    if (!r || !r.labKey || r.value == null) continue;
    const logical = `${r.labKey}|${r.date || ''}|${r.value}|${r.unit || ''}`;
    if (seen.has(logical)) continue;
    seen.add(logical);
    if (!byKey.has(r.labKey)) byKey.set(r.labKey, []);
    byKey.get(r.labKey).push(r);
  }
  for (const list of byKey.values()) list.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  return byKey;
}

// ── getMarkers (clinical-insights.mjs:760-819) ───────────────────────────────
/**
 * Per-CANON_LAB longitudinal series, one entry per lab that has ≥1 value. Points
 * are OLDEST→NEWEST and colour-coded via colorForValue against the prior reading.
 * @param labRows patient-wide shapeLabObservation() rows (any order).
 */
export function buildMarkers(labRows) {
  try {
    const byKey = labRowsByKey(labRows);
    const out = [];
    for (const labKey of Object.keys(CANON_LABS)) {
      const rows = byKey.get(labKey);
      if (!rows || !rows.length) continue;
      const chron = rows
        .map((o) => ({ value: num(o.value), date: o.date || null, interpretation: o.interpretation || 'unknown', sourceResourceId: o.sourceResourceId || o.id || null, derivedFromDocId: o.derivedFromDocId || null, unit: o.unit || null }))
        .filter((p) => p.value != null)
        .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
      if (!chron.length) continue;
      const meta = CANON_LABS[labKey] || {};
      let prev;
      const points = chron.map((p) => {
        const color = colorForValue(labKey, p.value, prev);
        prev = p.value;
        return { value: p.value, date: p.date, interpretation: p.interpretation, color, sourceResourceId: p.sourceResourceId, derivedFromDocId: p.derivedFromDocId };
      });
      const unit = points[points.length - 1]?.unit || meta.unit || chron[chron.length - 1].unit || '';
      out.push({
        labKey,
        display: meta.display || labKey,
        unit,
        refLow: meta.low ?? null,
        refHigh: meta.high ?? null,
        direction: meta.low != null && meta.low > 0 ? 'two-sided' : 'high',
        points,
        insight: markerInsight({ display: meta.display || labKey, coloured: points, unit, labKey }),
      });
    }
    return out;
  } catch { return []; }
}

// ── getTrends (clinical-insights.mjs:321-395) ────────────────────────────────
export function buildTrends(labRows) {
  try {
    const byKey = labRowsByKey(labRows);
    const out = [];
    for (const labKey of Object.keys(CANON_LABS)) {
      const rows = byKey.get(labKey);
      if (!rows || !rows.length) continue;
      const points = rows
        .map((o) => ({ value: num(o.value), unit: o.unit || CANON_LABS[labKey]?.unit || '', date: o.date || null, interpretation: o.interpretation || 'unknown', sourceResourceId: o.sourceResourceId || o.id || null, derivedFromDocId: o.derivedFromDocId || null, encounterId: o.encounterId || null }))
        .filter((p) => p.value != null)
        .sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
      if (!points.length) continue;
      const values = points.map((p) => p.value);
      const direction = directionOf(values, labKey);
      const delta = values.length >= 2 ? Math.round((values[values.length - 1] - values[0]) * 100) / 100 : null;
      const display = CANON_LABS[labKey]?.display || labKey;
      const unit = points[points.length - 1].unit || '';
      out.push({ labKey, display, points, direction, delta, note: trendNote({ labKey, display, unit, points, direction, delta }) });
    }
    const rank = { rising: 0, falling: 0, stable: 1, insufficient: 2 };
    return out.sort((a, b) => (rank[a.direction] - rank[b.direction]) || (b.points.length - a.points.length) || a.display.localeCompare(b.display));
  } catch { return []; }
}

// ── getVitalSeries (clinical-insights.mjs:828-904) ───────────────────────────
const VITAL_SERIES_DEFS = [
  { key: 'systolic', display: 'Systolic BP', unit: 'mmHg', loinc: '8480-6', component: true },
  { key: 'diastolic', display: 'Diastolic BP', unit: 'mmHg', loinc: '8462-4', component: true },
  { key: 'hr', display: 'Pulse', unit: 'beats/min', loinc: '8867-4' },
  { key: 'spo2', display: 'SpO₂', unit: '%', loinc: '59408-5' },
  { key: 'temp', display: 'Temperature', unit: '°C', loinc: '8310-5' },
  { key: 'weight', display: 'Weight', unit: 'kg', loinc: '29463-7' },
];

/** BP / pulse / SpO2 / weight / temp time-series across ALL the patient's
 *  encounters, from vital-signs Observation resources (RLS-fetched). */
export function buildVitalSeries(vitalObs) {
  try {
    const obs = [];
    const seen = new Set();
    for (const o of safeArr(vitalObs)) {
      if (!o || o.resourceType !== 'Observation') continue;
      const bag = healtherBag(o);
      const isVital = bag.kind === 'vital-sign'
        || safeArr(o.category).some((c) => safeArr(c.coding).some((x) => x.code === 'vital-signs'));
      if (!isVital) continue;
      if (o.id && seen.has(o.id)) continue;
      if (o.id) seen.add(o.id);
      obs.push(o);
    }
    if (!obs.length) return [];
    const out = [];
    for (const def of VITAL_SERIES_DEFS) {
      const pts = [];
      for (const o of obs) {
        const bag = healtherBag(o);
        const date = o.effectiveDateTime || (o.meta && o.meta.lastUpdated) || null;
        let value = null;
        if (def.component) {
          const comp = safeArr(o.component).find((c) => safeArr(c.code?.coding).some((x) => x.code === def.loinc));
          value = num(comp?.valueQuantity?.value);
        } else {
          const top = safeArr(o.code?.coding).some((x) => x.code === def.loinc) || bag.vital === def.key;
          if (top) value = num(o.valueQuantity?.value);
        }
        if (value == null) continue;
        pts.push({ value, date, sourceResourceId: o.id || null });
      }
      if (!pts.length) continue;
      pts.sort((a, b) => String(a.date || '').localeCompare(String(b.date || '')));
      out.push({ key: def.key, display: def.display, unit: def.unit, points: pts, insight: vitalInsight({ key: def.key, display: def.display, unit: def.unit, points: pts }) });
    }
    return out;
  } catch { return []; }
}

// ── getWhatChanged (clinical-insights.mjs:582-740, adapted to online-primary) ─
// Diffs THIS encounter against the patient's PREVIOUS visit. Adapted data source:
// meds + diagnosis come from the shaped visit rows (shapePatientHistory), lab
// moves from the patient-wide lab rows, and BP/weight moves from the patient-wide
// vital Observations (the old engine read those off the disk intake Composition;
// the Observations carry the same real values under RLS). Every item cites its
// source resource id + clinical date. Returns null when there is no prior visit.
function normMedKey(rawName) {
  return String(rawName || '').toLowerCase()
    .replace(/\b\d+(?:\.\d+)?\s*(?:mg|mcg|ml|gm?|iu|units?|%)\b/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}
const COND_QUALIFIERS = /\b(well[ -]?controlled|controlled|uncontrolled|stable|unstable|at[ -]?target|on[ -]?target|borderline|low|intermediate|high|very|raised|elevated|risk|mild|moderate|severe|chronic|acute|early|late|bilateral|left|right|grade\s*[0-9ivx]+|stage\s*[0-9ivx]+|class\s*[0-9ivx]+|provisional|suspected|r\/o|rule out|resolved|improving|worsening|since\s+[\w-]+|h\/o|history of|known)\b/gi;
function conditionCoreTerms(text) {
  return String(text || '').toLowerCase()
    .split(/[;,/()]|—|–|\band\b|\bwith\b|\+/)
    .map((part) => part.replace(COND_QUALIFIERS, '').replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim())
    .filter((t) => t.length >= 3);
}
function medsOfVisit(v) {
  const seen = new Set();
  const out = [];
  for (const m of safeArr(v && v.medications)) {
    const name = String((m && (m.name || m.medication)) || '').trim();
    if (!name) continue;
    const key = normMedKey(name);
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    out.push({ name, key, sourceResourceId: (m && (m.id || m.sourceResourceId)) || v.encounterId || null });
  }
  return out;
}

/**
 * @param visits      shapePatientHistory() rows, newest-first, INCLUDING current.
 * @param encounterId the current encounter's Medplum UUID.
 * @param labRows     patient-wide shapeLabObservation() rows.
 * @param vitalObs    patient-wide vital-signs Observation resources.
 */
export function buildWhatChanged({ visits, encounterId, labRows = [], vitalObs = [] } = {}) {
  try {
    const list = safeArr(visits);
    if (!list.length || !encounterId) return null;
    const curIdx = list.findIndex((v) => v && v.encounterId === encounterId);
    const cur = curIdx >= 0 ? list[curIdx] : null;
    // Prior = the first strictly-older visit after the current one (list is
    // newest-first). When the current encounter isn't in the list, treat the
    // most-recent OTHER visit as prior (mirrors the old edge-case handling).
    const priorList = curIdx >= 0 ? list.slice(curIdx + 1) : list.filter((v) => v && v.encounterId !== encounterId);
    const prior = priorList[0] || null;
    if (!prior) return null;
    const curDate = (cur && cur.date) || null;
    const priorDate = prior.date || null;
    const items = [];

    // Medications added / stopped.
    const curMeds = cur ? medsOfVisit(cur) : [];
    const priorMeds = medsOfVisit(prior);
    const priorKeys = new Set(priorMeds.map((m) => m.key).filter(Boolean));
    const curKeys = new Set(curMeds.map((m) => m.key).filter(Boolean));
    for (const m of curMeds) if (m.key && !priorKeys.has(m.key)) items.push({ kind: 'med-added', text: `Started ${m.name}`, sourceResourceId: m.sourceResourceId, date: curDate });
    for (const m of priorMeds) if (m.key && !curKeys.has(m.key)) items.push({ kind: 'med-stopped', text: `Stopped ${m.name} (on prior visit)`, sourceResourceId: m.sourceResourceId, date: priorDate });

    // New conditions — genuinely new only if NO core term was seen on ANY prior
    // visit's diagnosis (a re-worded ongoing dx must not resurface as "new").
    const everPrior = new Set();
    for (const v of priorList) for (const t of conditionCoreTerms(v.diagnosis)) everPrior.add(t);
    if (cur && cur.diagnosis) {
      const terms = conditionCoreTerms(cur.diagnosis);
      const isNew = terms.length > 0 && terms.every((t) => !everPrior.has(t));
      if (isNew) items.push({ kind: 'new-condition', text: `New diagnosis: ${cur.diagnosis}`, sourceResourceId: (cur.diagnosisResourceId || cur.encounterId) || null, date: curDate });
    }

    // Labs that moved (freshest vs freshest-older per lab; meaningful delta only).
    const byKey = labRowsByKey(labRows);
    for (const labKey of Object.keys(CANON_LABS)) {
      const rows = byKey.get(labKey);
      if (!rows || rows.length < 2) continue;
      const latest = rows[0];
      const previous = rows.find((r) => String(r.date || '') < String(latest.date || ''));
      if (!previous) continue;
      const a = num(previous.value); const b = num(latest.value);
      if (a == null || b == null) continue;
      const d = Math.round((b - a) * 100) / 100;
      const band = meaningfulDeltaFor(labKey);
      if (band == null || Math.abs(d) < band) continue;
      const unit = latest.unit || CANON_LABS[labKey]?.unit || '';
      const u = unit ? ` ${unit}` : '';
      const arrow = d > 0 ? '↑' : '↓';
      items.push({ kind: 'lab-moved', text: `${CANON_LABS[labKey]?.display || labKey} ${arrow} ${a}→${b}${u} (was ${isoDate(previous.date)}, interp ${latest.interpretation || 'unknown'})`, sourceResourceId: latest.sourceResourceId || latest.id || null, date: latest.date || curDate });
    }

    // BP + weight moves — from the patient-wide vital Observations.
    const bpSeries = buildVitalSeries(vitalObs).reduce((acc, s) => { acc[s.key] = s; return acc; }, {});
    const sys = bpSeries.systolic && bpSeries.systolic.points;
    const dia = bpSeries.diastolic && bpSeries.diastolic.points;
    if (sys && sys.length >= 2) {
      const latest = sys[sys.length - 1]; const prevPt = sys[sys.length - 2];
      const dSys = Math.round((latest.value - prevPt.value));
      if (Math.abs(dSys) >= 10) {
        const arrow = dSys > 0 ? '↑' : '↓';
        const dLatest = dia && dia[dia.length - 1]; const dPrev = dia && dia[dia.length - 2];
        items.push({ kind: 'bp', text: `BP ${arrow} ${prevPt.value}/${dPrev ? dPrev.value : '?'} → ${latest.value}/${dLatest ? dLatest.value : '?'} mmHg`, sourceResourceId: latest.sourceResourceId, date: latest.date || curDate });
      }
    }
    const wt = bpSeries.weight && bpSeries.weight.points;
    if (wt && wt.length >= 2) {
      const latest = wt[wt.length - 1]; const prevPt = wt[wt.length - 2];
      const dW = Math.round((latest.value - prevPt.value) * 10) / 10;
      if (Math.abs(dW) >= 2) {
        const arrow = dW > 0 ? '↑' : '↓';
        items.push({ kind: 'weight', text: `Weight ${arrow} ${prevPt.value} → ${latest.value} kg (Δ${dW > 0 ? '+' : ''}${dW})`, sourceResourceId: latest.sourceResourceId, date: latest.date || curDate });
      }
    }

    return { sinceDate: priorDate, items };
  } catch { return null; }
}

// ── insight bar / ranked (view-adapter.mjs:1177-1489, verbatim) ──────────────
function colorTrendPoints(trend) {
  const pts = safeArr(trend && trend.points);
  let prev;
  return pts.map((p) => {
    const color = colorForValue(trend.labKey, p.value, prev);
    prev = p.value;
    return { value: p.value, unit: p.unit || null, date: p.date || null, interpretation: p.interpretation || 'unknown', color, sourceResourceId: p.sourceResourceId || null, derivedFromDocId: p.derivedFromDocId || null };
  });
}
function worstColor(colored) { let worst = 'green'; for (const p of safeArr(colored)) if (COLOR_RANK[p.color] < COLOR_RANK[worst]) worst = p.color; return worst; }
function severityToColor(sev) { return sev === 'high' ? 'amber' : sev === 'medium' ? 'yellow' : 'green'; }
const CHANGE_KIND_RANK = { 'new-condition': 100, 'med-stopped': 92, 'med-added': 90, 'lab-moved': 80, bp: 70, weight: 60, lifestyle: 40 };
function changeKindScore(kind) { return CHANGE_KIND_RANK[kind] != null ? CHANGE_KIND_RANK[kind] : 50; }
function colorForChange(c) {
  const kind = c && c.kind;
  if (kind === 'new-condition' || kind === 'med-stopped') return 'amber';
  if (kind === 'lab-moved' || kind === 'bp') { const interp = String((c && c.text) || '').toLowerCase(); if (/critical|high|low|elevated|↑|↓/.test(interp)) return 'amber'; }
  return 'yellow';
}

/** Combine the raw engine outputs into the { trends, whatChanged, inconsistencies,
 *  safety } inputs the three insight builders share. */
export function gatherInsightInputs({ trends = [], whatChanged = null, inconsistencies = [], safety = [] } = {}) {
  return {
    trends: safeArr(trends),
    whatChanged,
    inconsistencies: safeArr(inconsistencies),
    safety: safeArr(safety),
  };
}

export function buildInsightsRanked(inputs) {
  if (!inputs) return null;
  const { trends, whatChanged, inconsistencies, safety } = inputs;
  const changeItems = [];
  if (whatChanged && Array.isArray(whatChanged.items)) {
    for (const c of whatChanged.items) {
      changeItems.push({
        key: `change:${c.kind}:${(c.sourceResourceId || '').toString().slice(0, 40)}:${(c.text || '').toString()}`,
        label: 'Since last visit', text: c.text || 'Change since last visit', color: colorForChange(c), kind: 'change',
        sourceResourceId: c.sourceResourceId || null, drill: { change: c }, _score: changeKindScore(c.kind),
      });
    }
  }
  changeItems.sort((a, b) => b._score - a._score);
  const top = changeItems.slice(0, 2).map(({ _score, ...rest }) => rest);
  const restChanges = changeItems.slice(2).map(({ _score, ...rest }) => rest);
  const rest = [];
  for (const w of safeArr(safety)) {
    rest.push({ key: `safety:${w.kind}:${(w.drug || '').toLowerCase()}:${(w.with || '').toLowerCase()}`, label: w.drug ? `${w.drug}${w.with ? ` + ${w.with}` : ''}` : 'Medication safety', text: w.message || 'Medication safety warning', color: severityToColor(w.severity), kind: 'safety', sourceResourceId: w.sourceResourceId || null, drill: null, _score: w.severity === 'high' ? 120 : w.severity === 'medium' ? 90 : 60 });
  }
  for (const t of safeArr(trends)) {
    const colored = colorTrendPoints(t);
    if (!colored.length) continue;
    const moved = t.direction === 'rising' || t.direction === 'falling';
    const color = worstColor(colored);
    if (color === 'green' && !moved) continue;
    rest.push({ key: `trend:${t.labKey}`, label: t.display || t.labKey, text: t.note || `${t.display || t.labKey} trend`, color, kind: 'trend', sourceResourceId: colored[colored.length - 1]?.sourceResourceId || null, drill: { labKey: t.labKey, points: colored }, _score: (color === 'amber' ? 80 : color === 'yellow' ? 50 : 20) + (moved ? 5 : 0) });
  }
  for (const f of safeArr(inconsistencies)) {
    rest.push({ key: `inconsistency:${f.id}`, label: f.title || 'Record inconsistency', text: f.explanation || f.title || 'Inconsistent records', color: severityToColor(f.severity), kind: 'inconsistency', sourceResourceId: (f.items && f.items[0] && f.items[0].resourceId) || null, drill: null, _score: f.severity === 'high' ? 110 : f.severity === 'medium' ? 70 : 45 });
  }
  for (const c of restChanges) rest.push({ ...c, _score: 40 });
  rest.sort((a, b) => b._score - a._score);
  const restClean = rest.map(({ _score, ...r }) => r);
  if (!top.length && !restClean.length && !(inconsistencies && inconsistencies.length)) return null;
  return { top, rest: restClean, inconsistencies: safeArr(inconsistencies) };
}

export function buildInsightsBar(inputs) {
  if (!inputs) return { changes: [], trends: [], inconsistencies: [] };
  try {
    const { trends, whatChanged, inconsistencies } = inputs;
    const changes = [];
    if (whatChanged && Array.isArray(whatChanged.items)) {
      for (const c of whatChanged.items) {
        if (c.kind === 'lab-moved') continue; // lab moves live in the RIGHT (trends) column
        changes.push({ key: `change:${c.kind}:${(c.sourceResourceId || '').toString().slice(0, 40)}:${(c.text || '').toString()}`, label: 'Since last visit', text: c.text || 'Change since last visit', color: colorForChange(c), kind: c.kind || 'change', sourceResourceId: c.sourceResourceId || null, drill: { change: c } });
      }
    }
    changes.sort((a, b) => changeKindScore(b.kind) - changeKindScore(a.kind));
    const trendsOut = [];
    for (const t of safeArr(trends)) {
      const colored = colorTrendPoints(t);
      if (!colored.length) continue;
      const moved = t.direction === 'rising' || t.direction === 'falling';
      const color = worstColor(colored);
      if (color === 'green' && !moved) continue;
      trendsOut.push({ key: `trend:${t.labKey}`, label: t.display || t.labKey, text: t.note || `${t.display || t.labKey} trend`, color, labKey: t.labKey, sourceResourceId: colored[colored.length - 1]?.sourceResourceId || null, drill: { labKey: t.labKey, points: colored, sources: colored.map((p) => p.sourceResourceId).filter(Boolean) } });
    }
    trendsOut.sort((a, b) => COLOR_RANK[a.color] - COLOR_RANK[b.color]);
    return { changes, trends: trendsOut, inconsistencies: safeArr(inconsistencies) };
  } catch { return { changes: [], trends: [], inconsistencies: [] }; }
}

export function buildInsightsBlock(inputs) {
  if (!inputs) return null;
  const { trends, whatChanged, inconsistencies, safety } = inputs;
  const movedTrends = safeArr(trends)
    .filter((t) => t && Array.isArray(t.points) && t.points.length >= 2 && (t.direction === 'rising' || t.direction === 'falling'))
    .map((t) => {
      const first = t.points[0]; const last = t.points[t.points.length - 1];
      return { labKey: t.labKey, display: t.display, direction: t.direction, delta: t.delta, note: t.note || null, from: { value: first.value, unit: first.unit, date: first.date, interpretation: first.interpretation, sourceResourceId: first.sourceResourceId }, to: { value: last.value, unit: last.unit, date: last.date, interpretation: last.interpretation, sourceResourceId: last.sourceResourceId } };
    });
  const block = { trends: movedTrends, whatChanged: whatChanged && Array.isArray(whatChanged.items) && whatChanged.items.length ? whatChanged : null, safety: safeArr(safety), inconsistencies: safeArr(inconsistencies) };
  if (!block.trends.length && !block.whatChanged && !block.safety.length && !block.inconsistencies.length) return null;
  return block;
}

// ── episode (view-adapter.mjs formatCarePlan/formatGoal + care-structures) ────
// Under online-primary RLS the doctor CANNOT read Goal resources (Goal is absent
// from the opd-doctor-rowlevel AccessPolicy — see opd-app/src/lib/care.js). So a
// consult's care goals are surfaced from the CarePlan's own goal[].display
// strings (what the doctor CAN read), and the visit's plan items from
// CarePlan.activity[]. We honestly do NOT fabricate goal progress (target/last
// value) — that needed the structured Goal.target the doctor can't fetch — so
// each goal renders with its description + a 'pending' status until a Goal read
// arm is added to the policy (deferred). The frontend Plan tab tolerates this:
// it shows the goal description + status badge and the "This visit's plan"
// activities, gating the whole tab on goals.length || activities.length.
/**
 * @param careDto the loadPatientCare() result ({ plan: shapeCarePlan(), ... }).
 * @returns { carePlan: { title, status, period, activities:[{text,dueIn,category,status}] },
 *            goals: [{ id, description, status, ... }] } | null when empty.
 */
export function buildEpisode(careDto) {
  try {
    const plan = careDto && careDto.plan;
    if (!plan) return null;
    const activities = safeArr(plan.activities).map((a) => ({
      text: a.description || a.text || '',
      dueIn: a.scheduled || a.dueIn || '',
      category: a.category || '',
      status: a.status || 'scheduled',
    })).filter((a) => a.text);
    const goals = safeArr(plan.goals).map((g, i) => ({
      id: g.ref || `goal-${i}`,
      description: g.display || 'Goal',
      measure: '', targetValue: null, targetUnit: '', targetText: '', dueDate: null,
      departmentId: '', departmentName: '',
      status: 'pending',
      latestValue: null, latestUnit: '', latestDate: null, derivedFromDocId: null,
      expressedBy: '',
    }));
    if (!goals.length && !activities.length) return null;
    return {
      carePlan: (activities.length || plan.title) ? { title: plan.title || 'Care plan', status: plan.status || 'active', period: plan.start ? { start: plan.start, ...(plan.end ? { end: plan.end } : {}) } : null, activities } : null,
      goals,
    };
  } catch { return null; }
}
